// Thrid Party
import * as React from 'react';

// Utils
import getConfig from '@config';
import { SubscriptionType, usePlansListAllLazyQuery } from '@gql';

type GqlPlan = {
  id: string;
  name: string;
  amount: number;
};

export type Plan = {
  id: string;
  name: string;
  amount: number;
  subscriptionType: SubscriptionType;
};

/**
 * @description This hook is used to get the price plans from the ENV.
 * It supports both lazy loading the plans from the API,
 * and receiving them as an argument for better performance.
 */
export function usePlans(plans: GqlPlan[]): {
  freePlan: Plan;
  bronzePlan: Plan;
  silverPlan: Plan;
  goldPlan: Plan;
  getPlanBySubscriptionType: (type: SubscriptionType) => Plan;
};
export function usePlans(): {
  freePlan: Plan | undefined;
  bronzePlan: Plan | undefined;
  silverPlan: Plan | undefined;
  goldPlan: Plan | undefined;
  getPlanBySubscriptionType: (type: SubscriptionType) => Plan | undefined;
};
export function usePlans(plans: GqlPlan[] = []) {
  const [plansListAllLazyQuery] = usePlansListAllLazyQuery();
  const [freePlan, setFreePlan] = React.useState<Plan | undefined>(undefined);
  const [bronzePlan, setBronzePlan] = React.useState<Plan | undefined>(
    undefined
  );
  const [silverPlan, setSilverPlan] = React.useState<Plan | undefined>(
    undefined
  );
  const [goldPlan, setGoldPlan] = React.useState<Plan | undefined>(undefined);

  const {
    publicRuntimeConfig: {
      stripe: {
        subscriptionProducts: {
          freeMonthly,
          bronzeMonthly,
          silverMonthly,
          goldMonthly,
        },
      },
    },
  } = getConfig();

  React.useEffect(() => {
    async function setPlans() {
      if (!plans) {
        const { data } = await plansListAllLazyQuery();
        const plans =
          data?.plansListAll.__typename === 'PlanCollection'
            ? data.plansListAll.entries
            : [];

        if (data) {
          const _freePlan = plans.find((plan) => plan.id === freeMonthly.id);
          if (_freePlan) {
            setFreePlan({
              ..._freePlan,
              subscriptionType: SubscriptionType.Free,
            });
          }

          const _bronzePlan = plans.find(
            (plan) => plan.id === bronzeMonthly.id
          );

          if (_bronzePlan) {
            setBronzePlan({
              ..._bronzePlan,
              subscriptionType: SubscriptionType.Bronze,
            });
          }

          const _silverPlan = plans.find(
            (plan) => plan.id === silverMonthly.id
          );

          if (_silverPlan) {
            setSilverPlan({
              ..._silverPlan,
              subscriptionType: SubscriptionType.Silver,
            });
          }

          const _goldPlan = plans.find((plan) => plan.id === goldMonthly.id);

          if (_goldPlan) {
            setGoldPlan({
              ..._goldPlan,
              subscriptionType: SubscriptionType.Gold,
            });
          }
        }
      }
    }
    void setPlans();
  }, [JSON.stringify(plans)]);

  if (plans) {
    const _freePlan = plans.find((plan) => plan.id === freeMonthly.id);

    const _bronzePlan = plans.find((plan) => plan.id === bronzeMonthly.id);

    const _silverPlan = plans.find((plan) => plan.id === silverMonthly.id);

    const _goldPlan = plans.find((plan) => plan.id === goldMonthly.id);

    if (!_freePlan) throw Error('Free plan is not set in the ENV.');
    if (!_bronzePlan) throw Error('Bronze plan is not set in the ENV.');
    if (!_silverPlan) throw Error('Silver plan is not set in the ENV.');
    if (!_goldPlan) throw Error('Gold plan is not set in the ENV.');

    const getPlanBySubscriptionType = (type: SubscriptionType) => {
      switch (type) {
        case SubscriptionType.Free:
          return { ..._freePlan, subscriptionType: SubscriptionType.Free };
        case SubscriptionType.Bronze:
          return { ..._bronzePlan, subscriptionType: SubscriptionType.Bronze };
        case SubscriptionType.Silver:
          return { ..._silverPlan, subscriptionType: SubscriptionType.Silver };
        case SubscriptionType.Gold:
          return { ..._goldPlan, subscriptionType: SubscriptionType.Gold };
        default:
          throw Error(
            `Plan is not set in the ENV for subscription type: ${type}.`
          );
      }
    };

    return {
      freePlan: { ..._freePlan, subscriptionType: SubscriptionType.Free },
      bronzePlan: { ..._bronzePlan, subscriptionType: SubscriptionType.Bronze },
      silverPlan: { ..._silverPlan, subscriptionType: SubscriptionType.Silver },
      goldPlan: { ..._goldPlan, subscriptionType: SubscriptionType.Gold },
      getPlanBySubscriptionType: getPlanBySubscriptionType,
    };
  } else {
    const getPlanBySubscriptionType = (type: SubscriptionType) => {
      switch (type) {
        case SubscriptionType.Free:
          return freePlan;
        case SubscriptionType.Bronze:
          return bronzePlan;
        case SubscriptionType.Silver:
          return silverPlan;
        case SubscriptionType.Gold:
          return goldPlan;
        default:
          return undefined;
      }
    };

    return {
      freePlan,
      bronzePlan,
      silverPlan,
      goldPlan,
      getPlanBySubscriptionType,
    };
  }
}
